import { HttpClient } from '@/core/classes/HttpClient'
import { Civility, OnboardingProgress, Staff } from '@/core/models/staff.model'
import { SubscriptionClassType } from '@/utils/subscription/subscription'
import { SubscriptionType } from '@/utils/subscription/types'
import { Tracker } from './Model'
import { Event } from '@/core/models/notification.model'
import { EntityState } from '@reduxjs/toolkit'
import { Admin } from '@/core/models/admin.model'
import { Patient } from '@/core/models/patient.model'
import { Slot } from '../models/schedule.model'
import { MedconnectAgentConfig } from '@/core/models/account.model'

export type LoginRequest = {
  username: string
  password: string
}

export type LoginResponse = {
  access_token: string
  refresh_token: string
}

export type AuthUser = (Admin | Patient | Staff) & {
  accountLinkUrl?: string
  // plugins: PluginTest[]
  onboardingProcess?: OnboardingProgress
}

export type UpdateAuthUserDto = {
  active?: boolean
  contactInfo?: {
    street?: string | null
    city?: string | null
    cp?: string | null
    phone?: string | null
  }
  userInfo?: {
    birthday?: string | null
    gender?: 'M' | 'F' | 'I'
    language?: string
    name?: string
    surname?: string
    notificationTimezone?: string
  }
  cps?: string | null
  ecgFolder?: string | null
  email?: string
  isFirstConnection?: boolean
  isEmailVerified?: boolean
  isPhoneVerified?: boolean
  photo?: string | null
  speciality?: String | null
  stripe?: string | null
  signature?: {
    data: string
    contentType: string
  }
  showPayment?: boolean
  displayChat?: boolean
  emergencyRoom?: string
  conclusionSentences?: string[]
  activeWaitingRooms?: string[]
  favoriteWaitingRooms?: string[]
  baudrate?: number
  bulkOrders?: string[]
  emailStatistics?: string[]
  managePatientPreferences?: {
    insEnabled?: boolean
  }
  medconnectAgentConfig?: MedconnectAgentConfig
  civility?: Civility | null
  title?: string | null
  conflictOfInterest?: string | null
}

export type OnlineStaff = Pick<Staff, 'userInfo' | 'establishment' | 'status' | 'speciality'> & {
  online: boolean
  socketId: string
  staffId: string
}

export type AuthState = {
  authUser: AuthUser | null
  calendsoPassword: string | null
  calcom: {
    link: string
    username: string
    password: string
  } | null
  availability: Pick<Slot, 'start' | 'end'>[]
  isAuthenticated: boolean
  isOfflineMode?: boolean
  requestTracker: Record<string, Tracker>
  tokens: {
    access: string | null
    refresh: string | null
  }
  onlineStaffs: OnlineStaff[]
  onlineUsers: string[]
  subscription: SubscriptionClassType
  events: EntityState<Event, Event['_id']>
  totalEvents: number
  magicLink?: string
}

export class Auth {
  private initialState: AuthState
  private httpClient: HttpClient

  static instance: Auth

  static getInstance() {
    if (!Auth.instance) {
      Auth.instance = new Auth()
    }
    return Auth.instance
  }

  constructor() {
    this.initialState = {
      authUser: null,
      calendsoPassword: null,
      isAuthenticated: false,
      calcom: null,
      availability: [],
      requestTracker: {
        create: { error: null, loading: false },
        get: {
          loading: false,
          error: null,
        },
        update: {
          loading: false,
          error: null,
        },
        login: {
          loading: false,
          error: null,
        },
      },
      tokens: {
        access: null, // "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiMCIsInVzZXJfYXV0aF9pZCI6IjAiLCJzYWx0IjoiNjA2MDUiLCJpYXQiOjE1MzA4MDEwNjV9.FxW8JRWyFu2QtKoO9MzdH9QhYRjgksXaYUEg6si1fK0",
        refresh: null, //"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiMCIsInVzZXJfYXV0aF9pZCI6IjAiLCJzYWx0IjoiNmM2NjZiMzdiZTY5IiwiaWF0IjoxNTMwODAxMDY1fQ.XxV0GhrkZpK1QsHHCwcRraSjUiVEOcZLHyU-k81FSrI"
      },
      onlineStaffs: [],
      onlineUsers: [],
      subscription: {
        type: SubscriptionType.NO_SUBSCRIPTION,
      },
      events: {
        ids: [],
        entities: {},
      },
      totalEvents: 0,
    }
    this.httpClient = new HttpClient()
  }

  getInitialState(): AuthState {
    return this.initialState
  }

  getAccountLink = async (accountLinkParams: { return_url?: string; refresh_url?: string } = {}) => {
    return await this.httpClient.request<string>('/charge/connect', 'GET', accountLinkParams)
  }

  getCalendsoPassword = async (staffId: string): Promise<string> => {
    return await this.httpClient.request(`/calendso-p/pass/${staffId}`, 'GET')
  }

  // TODO: replace return type
  getMe = async (): Promise<AuthUser> => {
    return await this.httpClient.request<AuthUser>('/auth/me', 'GET')
  }

  updateMyProfile = async (updateUserDto: UpdateAuthUserDto) => {
    return await this.httpClient.request('/auth/me', 'PATCH', updateUserDto)
  }

  login = async (data: LoginRequest): Promise<LoginResponse> => {
    return await this.httpClient.request('/auth/login', 'POST', data)
  }

  resetStealthLogin = async (identifiant: string): Promise<any> => {
    return await this.httpClient.request('/auth/success-magic', 'POST', {
      identifiant,
    })
  }

  startTwilioVerify = async (to: string, channel: 'email' | 'sms') => {
    return await this.httpClient.request('/auth/start-twilio-verify', 'POST', { to, channel })
  }

  checkTwilioVerify = async (to: string, code: string) => {
    return await this.httpClient.request('/auth/check-twilio-verify', 'POST', { to, code })
  }

  getCalcom = async () => {
    return await this.httpClient.request('/calendso-p/link', 'GET')
  }

  getMyAvailibility = async () => {
    return await this.httpClient.request<Slot[]>('/calendso-p/availability', 'GET')
  }

  updateMyAvailibility = async (availibility: Slot[]) => {
    return await this.httpClient.request<Slot[]>('/calendso-p/availability', 'PATCH', availibility)
  }

  getMyEvents = async (limit: number, skip: number) => {
    return this.httpClient.request<[Event[], number]>(`/auth/me/notifications?limit=${limit}&skip=${skip}`, 'GET')
  }

  readMyEvents = async (eventId: string) => {
    return this.httpClient.request<Event>(`/auth/me/notifications/${eventId}`, 'PATCH')
  }

  getMagicLink = async (redirectUri: string) => {
    return await this.httpClient.request<{ url: string }>(`/auth/magic-link?redirect_uri=${encodeURIComponent(redirectUri)}`, 'GET')
  }
}

export default Auth.getInstance()
